import React, { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataContext from "../../Context";
import useSaifFetch from "../../hooks/useSaifFetch";
import LocalizationContext from "../../Localization/LanguageContext";
import { ILocalizationContext } from "../../Localization/Models/ILocalizationContext";
import { IPolicy } from "../../Models/PolicyInfo";
import { API_ACTION, SaifFetch } from "../../Utils/SaifFetch";
import Answer from "./answer";
import Question from "./question";

interface Props {
  setSecurityQuestionsAreValid: React.Dispatch<React.SetStateAction<boolean>>;
  required: boolean;
}

const SecurityQuestions: FC<Props> = ({
  setSecurityQuestionsAreValid,
  required,
}) => {
  const {
    question1,
    securityQuestions,
    setSecurityQuestions,
    setQuestion1,
    question1Answer,
    setQuestion1Answer,
    question2,
    setQuestion2,
    question2Answer,
    setQuestion2Answer,
    question3,
    setQuestion3,
    question3Answer,
    setQuestion3Answer,
    isSanitized,
  } = useContext(DataContext) as IPolicy;
  const { getValue } = useContext(LocalizationContext) as ILocalizationContext;

  let navigator = useNavigate();

  const [isAns1Required, setIsAns1Required] = useState(false);
  const [isAns2Required, setIsAns2Required] = useState(false);
  const [isAns3Required, setIsAns3Required] = useState(false);
  const [isQ1Required, setIsQ1Required] = useState(false);
  const [isQ2Required, setIsQ2Required] = useState(false);
  const [isQ3Required, setIsQ3Required] = useState(false);
  const [shouldGetSecurityQuestions, getSecurityQuestions] = useState({});
  const { data: securityQuestionData, status: securityQuestionStatus, error } =
    useSaifFetch<any>(
      API_ACTION.SecurityQuestions,
      shouldGetSecurityQuestions,
      SaifFetch.get
    );

  useEffect(() => {
    if (required) {
      setIsAns1Required(true);
      setIsAns2Required(true);
      setIsAns3Required(true);
      setIsQ1Required(true);
      setIsQ2Required(true);
      setIsQ3Required(true);
    }
  }, [required]);

  useEffect(() => {
    if (securityQuestions == null) {
      getSecurityQuestions({ default: true });
    }
  }, []);

  useEffect(() => {
    if (securityQuestionStatus === 200) {
      setSecurityQuestions(securityQuestionData);
    } 
    else if ((securityQuestionStatus && securityQuestionStatus >= 400) || error) {
      console.log("Failed to fetch security questions");
      navigator("/error", { replace: true });
    }
  }, [securityQuestionStatus]);

  useEffect(() => {
    if (
      question1Answer.trim().length > 0 &&
      question2Answer.trim().length > 0 &&
      question3Answer.trim().length > 0 &&
      question1.questionText !== "" &&
      question2.questionText !== "" &&
      question3.questionText !== "" &&
      isSanitized(question1Answer) &&
      isSanitized(question2Answer) &&
      isSanitized(question3Answer)
    ) {
      setSecurityQuestionsAreValid(true);
    } else {
      setSecurityQuestionsAreValid(false);
    }
  }, [
    question1Answer,
    question2Answer,
    question3Answer,
    question1,
    question2,
    question3,
  ]);

  return (
    <>
      <Question
        id="question1"
        label={getValue('SecurityQuestions', 'Question1Label')}
        ignoreQuestion1={question2}
        ignoreQuestion2={question3}
        allQuestions={securityQuestions}
        question={question1}
        setQuestion={setQuestion1}
        required={isQ1Required}
        setRequired={setIsQ1Required}
      />
      <Answer
        id="question1Answer"
        answer={question1Answer}
        setAnswer={setQuestion1Answer}
        isRequired={isAns1Required}
        setIsRequired={setIsAns1Required}
      />
      <Question
        id="question2"
        label={getValue('SecurityQuestions', 'Question2Label')}
        ignoreQuestion1={question1}
        ignoreQuestion2={question3}
        allQuestions={securityQuestions}
        question={question2}
        setQuestion={setQuestion2}
        required={isQ2Required}
        setRequired={setIsQ2Required}
      />
      <Answer
        id="question2Answer"
        answer={question2Answer}
        setAnswer={setQuestion2Answer}
        isRequired={isAns2Required}
        setIsRequired={setIsAns2Required}
      />
      <Question
        id="question3"
        label={getValue('SecurityQuestions', 'Question3Label')}
        ignoreQuestion1={question1}
        ignoreQuestion2={question2}
        allQuestions={securityQuestions}
        question={question3}
        setQuestion={setQuestion3}
        required={isQ3Required}
        setRequired={setIsQ3Required}
      />
      <Answer
        id="question3Answer"
        answer={question3Answer}
        setAnswer={setQuestion3Answer}
        isRequired={isAns3Required}
        setIsRequired={setIsAns3Required}
      />
    </>
  );
};

export default SecurityQuestions;
